import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

const quickCats = [
  { label: "Todos", slug: "", icon: "ri-apps-2-line" },
  { label: "Eletrônicos", slug: "eletronicos", icon: "ri-tv-2-line" },
  { label: "Smartphones", slug: "smartphones", icon: "ri-smartphone-line" },
  { label: "Notebooks", slug: "notebooks", icon: "ri-macbook-line" },
  { label: "Games", slug: "games", icon: "ri-gamepad-line" },
  { label: "Eletrodomésticos", slug: "eletrodomesticos", icon: "ri-fridge-line" },
  { label: "Fones", slug: "fones-de-ouvido", icon: "ri-headphone-line" },
  { label: "Smartwatches", slug: "smartwatches", icon: "ri-watch-line" },
  { label: "Casa", slug: "casa-cozinha", icon: "ri-home-heart-line" },
  { label: "Ofertas", slug: "ofertas", icon: "ri-price-tag-3-line" },
];

interface CategoryBarProps {
  onCategoryChange?: (slug: string) => void;
  activeCategory?: string;
}

export default function CategoryBar({ onCategoryChange, activeCategory = "" }: CategoryBarProps) {
  const [isSticky, setIsSticky] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const handleClick = (slug: string) => {
    if (slug === "ofertas") {
      navigate("/ofertas");
      return;
    }
    if (onCategoryChange) {
      onCategoryChange(slug);
      return;
    }
    if (!slug) {
      navigate("/categorias");
      return;
    }
    navigate(`/categoria/${slug}`);
  };

  return (
    <>
      <div ref={sentinelRef} className="h-0" />

      <div
        ref={barRef}
        className={`w-full z-40 transition-all duration-200 ${
          isSticky
            ? "sticky top-16 bg-white border-b border-gray-100 shadow-sm"
            : "bg-white border-b border-gray-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-2">
            {quickCats.map((cat) => {
              const isActive = activeCategory === cat.slug;
              return (
                <button
                  key={cat.slug || "all"}
                  onClick={() => handleClick(cat.slug)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                    isActive
                      ? "bg-orange-500 text-white"
                      : cat.slug === "ofertas"
                      ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.label}
                  {cat.slug === "ofertas" && (
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                  )}
                </button>
              );
            })}

            <div className="ml-auto flex-shrink-0 pl-3 border-l border-gray-100">
              <Link
                to="/categorias"
                className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 font-semibold whitespace-nowrap transition-colors"
              >
                Ver todas
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
